import React, { useEffect, useRef, useState } from 'react';

import { DatePicker as AntDatePicker, Checkbox, Col, Input, Row, Select, Switch, Tooltip, Typography } from 'antd';

import CharacterCounter from 'components/CharacterCounter';
import CropUploader from 'components/CropUploader';
import CustomMaskedInput from 'components/MaskedInput';
import dayjs from 'dayjs';
import { constants } from 'helpers';
import { removeDoubleSpace } from 'helpers/functions';
import type IProduct from 'interfaces/IProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import { Wysiwyg } from 'v2/components/Shared/Wysiwyg';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { FormContent } from '../FormContent';
import BlockedFieldTooltipProps from './blockedTooltipProps';
import MarketplaceValidation from './marketplaceValidation';
import Validations from './validations';

interface IProps {
  loading: boolean;
  fade?: boolean;
  product?: IProduct;
  handleImageChange?: any;
  handleUploadEvents?: any;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  categoryList?: any;
  onChangeRequiredFields?: any;
  variations?: IProduct[];
}

function htmlDecode(input: any) {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
}

function General(props: IProps) {
  const { loading, product, handleImageChange, handleChange, categoryList, variations, onChangeRequiredFields } = props;

  const [validationErrors, setValidationErrors] = useState<any>(() => run(props.product, Validations));
  const prevPropsRef = useRef({ loading, product });

  const errorFor = (field: any) => {
    return validationErrors[field] || '';
  };

  const isValid = (field: any) => {
    return validationErrors[field] ? true : false;
  };

  const handlePreLaunchChange = (isChecking: boolean) => {
    if (handleChange) {
      handleChange('prelaunch', isChecking);
      handleChange('launch_date', isChecking ? new Date() : '');
    }
  };

  useEffect(() => {
    const prevProps = prevPropsRef.current;
    const shouldUpdate = (!loading && prevProps.loading) || product !== prevProps.product;

    if (shouldUpdate) {
      let newValidationErrors = run(product, Validations);

      if (variations && variations.some(variation => variation.moderation_status === 'A') && !product.parent_id) {
        const marketplaceErrors = run(product, MarketplaceValidation);
        newValidationErrors = { ...newValidationErrors, ...marketplaceErrors };
      }

      setValidationErrors(newValidationErrors);

      if (onChangeRequiredFields) {
        onChangeRequiredFields(
          'generalRequiredFields',
          newValidationErrors ? Object.keys(newValidationErrors).length : 0
        );
      }
    }

    prevPropsRef.current = { loading, product };
  }, [loading, product, variations, onChangeRequiredFields]);

  const isVariation = product.isVariation();
  const isMarketplaceBeta = eduzzAccountsService.verifyUserHasBetaModule('marketplace_eduzz');
  const isAlumy = product.cnt_partner_cod === EPartnerCode.ALUMY;
  const isBlinket = product.cnt_partner_cod === EPartnerCode.BLINKET;
  const isService = product.cnt_partner_cod === EPartnerCode.SERVICE;
  const noEditMessage = 'Só é possível alterar o campo em configurações gerais';
  const categories = Array.isArray(categoryList) ? categoryList : [];
  const approvedVariations = variations?.filter(variation => variation.moderation_status === 'A');
  const deliveries = product.getDeliveries();
  const hasNutrorDelivery = Array.isArray(deliveries)
    ? deliveries.some(delivery => delivery.method_name === 'Nutror')
    : false;

  return (
    <Row gutter={[16, 16]} align='middle'>
      <Col span={24}>
        <FormContent title={isService ? 'Dados básicos do serviço' : 'Dados básicos do produto'} loading={loading}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy || isBlinket}>
                <div>
                  <div className='relative'>
                    <label className='mb-1 block text-sm'>
                      {isService ? 'Título do serviço' : 'Título do produto'}
                    </label>
                    <Input
                      id='title'
                      className='w-full'
                      status={isValid('title') ? 'error' : undefined}
                      value={product.title}
                      disabled={isAlumy || isBlinket}
                      onChange={e => handleChange('title', e.target.value)}
                    />
                    <p className='mt-1 text-xs text-gray-500'>
                      {isValid('title')
                        ? errorFor('title')
                        : 'Título curto do que está sendo vendido: Especialista em Vendas 1.0'}
                    </p>
                  </div>
                </div>
              </BlockedFieldTooltipProps>
            </Col>
            <Col span={24}>
              <Tooltip
                title={isService ? 'Tipo de serviço não pode ser alterado' : 'Tipo de produto não pode ser alterado'}
              >
                <div className='relative'>
                  <label className='mb-1 block text-sm'>{isService ? 'Tipo do serviço' : 'Tipo do produto'}</label>
                  <Select
                    id='type'
                    className='w-full'
                    disabled={true}
                    value={product.type}
                    onChange={value => handleChange('type', value)}
                    options={[
                      { value: 1, label: 'Infoproduto' },
                      { value: 2, label: 'Serviço', disabled: true },
                      { value: 3, label: 'Produto Físico', disabled: true },
                      { value: 4, label: 'Ticket', disabled: true }
                    ]}
                  />
                  <p className='mt-1 text-xs text-gray-500'>
                    Ex: tipo do produto vendido: infoproduto, físico, serviço, ticket...
                  </p>
                </div>
              </Tooltip>
            </Col>
            {!isBlinket && (
              <Col span={24}>
                <div className='relative'>
                  <label className={`mb-1 block text-sm ${isValid('categories') ? 'text-red-500' : ''}`}>
                    Categorias
                  </label>
                  <Select
                    mode='multiple'
                    className='w-full'
                    status={isValid('categories') ? 'error' : undefined}
                    value={product.getIdCategories()}
                    onChange={value => handleChange('categories', value)}
                    labelRender={props => categories.find(c => c.id === props.value)?.name || props.label}
                  >
                    {categories.map((item: any) => (
                      <Select.Option key={'catmn' + item.id} value={item.id}>
                        <Checkbox checked={product.getIdCategories().includes(item.id)} className='mr-2!' />
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                  {isValid('categories') && <p className='mt-1 text-xs text-red-500'>{errorFor('categories')}</p>}
                </div>
              </Col>
            )}
            <Col span={24}>
              <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy || isBlinket}>
                <div>
                  <CharacterCounter
                    id={product.id}
                    value={htmlDecode(product.description)}
                    limits={4000}
                    minimum={isBlinket ? 50 : 100}
                    helperText={
                      isValid('description')
                        ? errorFor('description')
                        : 'Pequena descrição sobre seu produto/serviço, seus benefícios, etc'
                    }
                    disabled={isAlumy || isBlinket}
                    label={isService ? 'Descrição do serviço' : 'Descrição do produto'}
                    error={isValid('description')}
                    onChange={(e: any) => handleChange('description', removeDoubleSpace(e.target.value))}
                    singleSpace={true}
                  />
                </div>
              </BlockedFieldTooltipProps>
            </Col>
            <Col span={24}>
              <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                <div>
                  <label className={`mb-1 block text-sm ${isValid('image_id') ? 'text-red-500' : ''}`}>
                    {isService ? 'Imagem do serviço' : 'Imagem do produto'}
                  </label>
                  <CropUploader
                    name='image'
                    id='name'
                    image={product.image}
                    onChange={handleImageChange}
                    fileName='image_id'
                    uploadType='cnt_foto'
                    disabled={isBlinket}
                  />
                  {isValid('image_id') && <p className='mt-1 text-xs text-red-500'>{errorFor('image_id')}</p>}
                </div>
              </BlockedFieldTooltipProps>
            </Col>
          </Row>
        </FormContent>
      </Col>
      <Col span={24}>
        <FormContent title={isService ? 'Detalhes do serviço' : 'Detalhes do produto'} loading={loading}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                <div className='flex items-center gap-2'>
                  <Switch checked={product.prelaunch} onChange={handlePreLaunchChange} disabled={isBlinket} />
                  <span>{isService ? 'Serviço será lançamento' : 'Produto será lançamento'}</span>
                </div>
              </BlockedFieldTooltipProps>
            </Col>
            {product.prelaunch && (
              <>
                <Col span={24}>
                  <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                    <div>
                      <Typography.Text className='mb-1 block text-base'>Data do lançamento</Typography.Text>
                      <AntDatePicker
                        className='w-full'
                        format={{
                          format: isBlinket ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY',
                          type: 'mask'
                        }}
                        placeholder='DD/MM/YYYY'
                        value={product.launch_date ? dayjs(product.launch_date) : undefined}
                        disabled={isBlinket}
                        disabledDate={current => !!current && current.isBefore(dayjs().startOf('day'))}
                        onChange={date => handleChange('launch_date', date ? date.toDate() : '')}
                      />
                      <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                        Se o lançamento estiver ativo o checkout ficará indisponível até a data informada.
                      </Typography.Paragraph>
                    </div>
                  </BlockedFieldTooltipProps>
                </Col>
                <Col span={24}>
                  <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket || isAlumy}>
                    <div>
                      <CustomMaskedInput
                        fullWidth
                        disableUnderline
                        label='Número de vendas esperadas'
                        mask='number'
                        value={String(product.sales_expectation)}
                        disabled={isAlumy || isBlinket}
                        onChange={(value: any) => handleChange('sales_expectation', value)}
                        helperText='Ex: Quantas vendas estima fazer durante o lançamento?'
                      />
                    </div>
                  </BlockedFieldTooltipProps>
                </Col>
              </>
            )}
            {!product.isMainWithChildren() && (
              <>
                <Col span={24}>
                  <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                    <div className='flex items-center gap-2'>
                      <Switch
                        checked={product.has_expiration_date}
                        onChange={checked => handleChange('has_expiration_date', checked)}
                        disabled={isBlinket}
                      />
                      <span>{isService ? 'Validade do serviço' : 'Validade do produto'}</span>
                    </div>
                  </BlockedFieldTooltipProps>
                </Col>
                {product.has_expiration_date && (
                  <Col span={24}>
                    <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                      <div>
                        <Typography.Text className='mb-1 block text-base'>Data de validade</Typography.Text>
                        <AntDatePicker
                          className='w-full'
                          format={{
                            format: isBlinket ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY',
                            type: 'mask'
                          }}
                          placeholder='DD/MM/YYYY'
                          value={product.expiration_date ? dayjs(product.expiration_date) : undefined}
                          onChange={date => handleChange('expiration_date', date ? date.toDate() : '')}
                          disabledDate={current => !!current && current.isBefore(dayjs().startOf('day'))}
                          disabled={!product.has_expiration_date || isBlinket}
                        />
                        <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                          Data final da comercialização do conteúdo
                        </Typography.Paragraph>
                      </div>
                    </BlockedFieldTooltipProps>
                  </Col>
                )}
              </>
            )}
            {isMarketplaceBeta && hasNutrorDelivery && (
              <>
                {!isVariation && product.moderation_status === 'A' && (
                  <Col span={24}>
                    <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy || isBlinket}>
                      <div>
                        <div className='flex items-center gap-2'>
                          <Switch
                            checked={product.marketplace}
                            onChange={checked => handleChange('marketplace', checked)}
                            disabled={isAlumy || isBlinket}
                          />
                          <span>Apresentar no Marketplace do site da Eduzz</span>
                        </div>
                        <p className='mt-1 text-xs text-gray-500'>
                          Ao ativar esta opção o seu produto será apresentado no site da Eduzz na opção Marketplace
                        </p>
                      </div>
                    </BlockedFieldTooltipProps>
                  </Col>
                )}
                {product.marketplace && !isVariation && approvedVariations?.length >= 1 && (
                  <Col span={24}>
                    <div>
                      <label className='mb-1 block text-sm'>Variação do produto</label>
                      <Select
                        className='w-full'
                        status={isValid('marketplace_variation') ? 'error' : undefined}
                        value={product.marketplace_variation}
                        onChange={value => handleChange('marketplace_variation', value)}
                        options={approvedVariations?.map(variation => ({
                          value: variation.id,
                          label: variation.title
                        }))}
                      />
                      <p className='mt-1 text-xs text-gray-500'>
                        {isValid('marketplace_variation')
                          ? errorFor('marketplace_variation')
                          : 'Selecione qual variação do produto será apresentada no Marketplace'}
                      </p>
                    </div>
                  </Col>
                )}
              </>
            )}
            {[constants.INFO, constants.TICKET, constants.SERVICE, constants.PHYSICAL].includes(product.type) && (
              <Col span={24}>
                <Tooltip title={isVariation ? noEditMessage : ''}>
                  <div className='relative'>
                    <label className='mb-1 block text-sm'>
                      {product.type !== constants.PHYSICAL
                        ? 'Nome do autor'
                        : 'Nome do responsável pelo produto no checkout'}
                    </label>
                    <Input
                      id='author_name'
                      className='w-full'
                      disabled={isVariation}
                      value={product.author_name}
                      onChange={e => handleChange('author_name', e.target.value)}
                    />
                    <p className='mt-1 text-xs text-gray-500'>
                      {product.type !== constants.PHYSICAL
                        ? 'Ex: José Pereira'
                        : 'Este nome será visível na tela de pagamento para que o comprador saiba exatamente de quem está adquirindo o produto.'}
                    </p>
                  </div>
                </Tooltip>
              </Col>
            )}
            {product.type === constants.INFO && !product.isMainWithChildren() && (
              <Col span={24}>
                <label className={`mb-2 block text-sm ${isValid('program_content') ? 'text-red-500' : ''}`}>
                  O que contém o seu produto/serviço
                </label>
                <Wysiwyg value={product.program_content} onChange={(v: string) => handleChange('program_content', v)} />
                <p className={`mt-1 text-xs ${isValid('program_content') ? 'text-red-500' : 'text-gray-500'}`}>
                  {isValid('program_content')
                    ? errorFor('program_content')
                    : 'Ex: descrição sobre os componentes do produto. (Máximo de 4000 caracteres)'}
                </p>
              </Col>
            )}
          </Row>
        </FormContent>
      </Col>

      {!product.isMainWithChildren() && (
        <Col span={24}>
          <FormContent title='Suporte e reembolso' loading={loading}>
            <Row gutter={[16, 16]} align='middle'>
              {product.type === constants.INFO && (
                <Col span={24}>
                  <div className='flex items-center gap-2'>
                    <Switch checked={product.auto_refund} onChange={checked => handleChange('auto_refund', checked)} />
                    <span>Aprovação automática do reembolso</span>
                  </div>
                  <p className='mt-1 text-xs text-gray-500'>
                    Essa opção ativada autoriza que os reembolsos solicitados pelo comprador até o prazo configurado
                    sejam realizados automaticamente
                  </p>
                </Col>
              )}
              <Col span={24}>
                <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isBlinket}>
                  <div className='relative'>
                    <label className='mb-1 block text-sm'>E-mail de suporte</label>
                    <Input
                      id='support_email'
                      className='w-full'
                      status={isValid('support_email') ? 'error' : undefined}
                      value={product.support_email}
                      disabled={isBlinket}
                      onChange={e => handleChange('support_email', e.target.value)}
                    />
                    <p className='mt-1 text-xs text-gray-500'>
                      {isValid('support_email')
                        ? errorFor('support_email')
                        : 'E-mail que será exibido para seus compradores e que receberá os e-mails de contato do suporte.'}
                    </p>
                  </div>
                </BlockedFieldTooltipProps>
              </Col>
              <Col span={24}>
                <div className='relative'>
                  <label className='mb-1 block text-sm'>Prazo para reembolso</label>
                  <Input
                    className='w-full text-right'
                    status={isValid('refund_days') ? 'error' : undefined}
                    value={product.refund_days}
                    onChange={e => {
                      let value = e.target.value;

                      if (value) {
                        value = value.replace(/\D/g, '');
                      }

                      handleChange('refund_days', value);
                    }}
                  />
                  <p className='mt-1 text-xs text-gray-500'>
                    {isValid('refund_days')
                      ? errorFor('refund_days')
                      : 'Prazo que o cliente poderá pedir reembolso. Mínimo de 7 dias'}
                  </p>
                </div>
              </Col>
            </Row>
          </FormContent>
        </Col>
      )}
      {product.type === constants.PHYSICAL && !product.isMainWithChildren() && (
        <Col span={24}>
          <FormContent title='Estoque' loading={loading}>
            <Row gutter={[16, 16]} align='middle'>
              <Col span={24}>
                <div className='relative'>
                  <label className='mb-1 block text-sm'>Quantidade de estoque</label>
                  <Input
                    className='w-full text-right'
                    status={isValid('stock') ? 'error' : undefined}
                    value={String(product.stock)}
                    onChange={e => {
                      let value = e.target.value;

                      if (value) {
                        value = value.replace(/\D/g, '');
                      }

                      handleChange('stock', value);
                    }}
                  />
                  {isValid('stock') && <p className='mt-1 text-xs text-red-500'>{errorFor('stock')}</p>}
                </div>
              </Col>
              <Col span={24}>
                <div className='relative'>
                  <label className='mb-1 block text-sm'>SKU</label>
                  <Input
                    id='sku'
                    className='w-full'
                    status={isValid('sku') ? 'error' : undefined}
                    value={product.sku}
                    onChange={e => handleChange('sku', e.target.value)}
                  />
                  <p className='mt-1 text-xs text-gray-500'>
                    {isValid('sku') ? errorFor('sku') : 'Unidade de Manutenção de Estoque'}
                  </p>
                </div>
              </Col>

              {product.stock > 0 && (
                <Col span={24}>
                  <div className='flex items-center gap-2'>
                    <Switch
                      checked={product.notify_stock > 0}
                      onChange={checked =>
                        handleChange(
                          'notify_stock',
                          checked ? (product.notify_stock > 0 ? product.notify_stock : 1) : 0
                        )
                      }
                    />
                    <span>Notificar quando estoque estiver acabando</span>
                  </div>
                </Col>
              )}
              {product.stock > 0 && product.notify_stock > 0 && (
                <Col span={24}>
                  <CustomMaskedInput
                    fullWidth
                    disableUnderline
                    label='Notificar ao chegar em'
                    helperText={isValid('notify_stock') ? errorFor('notify_stock') : ''}
                    mask='number'
                    error={isValid('notify_stock')}
                    value={String(product.notify_stock)}
                    onChange={(value: any) => handleChange('notify_stock', value)}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Col>
              )}
            </Row>
          </FormContent>
        </Col>
      )}
    </Row>
  );
}

export default React.memo(General);
